import React, { useState, useMemo, useEffect } from 'react'
import { Icon, Popover, PopoverContent, PopoverTrigger, Input, IconButton } from '@shopflo/ui'
import { MagnifyingGlass, Trash, Tag, SealPercent } from '@phosphor-icons/react'
import { useDiscountForm, useScrollToErrorRef } from '../../hooks/useDiscountForm'
import { apiURI, DiscountCallArgCouponType } from 'src/lib/utils/constants'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import useSWRInfinite from 'swr/infinite'
import { fetcher, postRequest, staticOptions } from 'src/lib/core/api-client'
import { truncateString } from 'src/lib/utils/helpers'
import { debounce } from 'lodash'
import { CouponSummaryResponse, DiscountStackMode } from 'src/lib/types/discountsV2'
import FormErrorText from 'src/components/common/FormErrorText'
import router, { useRouter } from 'next/router'
import useSWR from 'swr'

const PAGE_SIZE = 10

interface DiscountRaw {
  coupon_id: string
  code: string
  title: string
  active: boolean
  auto_applicable: boolean
}

interface DiscountListResponse {
  data: DiscountRaw[]
  total_count: number
  max_pages: number
  success: boolean
}

const CustomDiscountSelector = () => {
  const {
    state: { prerequisite, errors },
    actions: { setFormState }
  } = useDiscountForm()

  const scrollRef = useScrollToErrorRef(
    'prerequisite.stackability.customStackableDiscountIds'
  ) as React.RefObject<HTMLDivElement>

  const {
    getters: { merchant }
  } = useMerchant()
  const router = useRouter()
  const { discountId } = router.query

  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearchTerm(value), 300), [])

  useEffect(() => {
    debouncedSetSearch(searchTerm)
    return () => debouncedSetSearch.cancel()
  }, [searchTerm, debouncedSetSearch])

  const activeCustomIds =
    prerequisite.stackability.stackMode === DiscountStackMode.CUSTOM
      ? prerequisite.stackability.customStackableDiscountIds
      : []

  const { data: summariesData } = useSWR(
    () => {
      if (activeCustomIds.length > 0 && merchant?.accountId && discountId) {
        return `/issuer/${issuerId}/merchant/${merchant.accountId}/coupon/summaries`
      }
      return null
    },
    async url => {
      const res = await postRequest(url, apiURI.DISCOUNT_ENGINE, activeCustomIds)
      return res.data as CouponSummaryResponse
    },
    { ...staticOptions }
  )

  const getKey = (pageIndex: number, previousPageData: DiscountListResponse | null) => {
    if (previousPageData && pageIndex >= previousPageData.max_pages) return null
    if (!merchant?.accountId) return null

    const page = pageIndex + 1
    const searchParams = debouncedSearchTerm
      ? `&title=${encodeURIComponent(debouncedSearchTerm)}&coupon_code=${encodeURIComponent(debouncedSearchTerm)}`
      : ''

    const query = `/issuer/${issuerId}/merchant/${merchant.accountId}/coupon?offset=${page}&limit=${PAGE_SIZE}&coupon_type=${DiscountCallArgCouponType}&stack_types=CUSTOM,ALL${searchParams}`
    return query
  }

  const { data, size, setSize, isLoading, isValidating } = useSWRInfinite<DiscountListResponse>(
    getKey,
    url => fetcher(url, apiURI.DISCOUNT_ENGINE) as Promise<DiscountListResponse>,
    {
      revalidateFirstPage: false
    }
  )

  const { stackability } = prerequisite

  if (stackability.stackMode !== DiscountStackMode.CUSTOM) {
    return null
  }

  const { customStackableDiscountIds } = stackability

  const discounts = data ? data.flatMap(page => page?.data || []) : []
  const isReachingEnd = data && size >= data[0]?.max_pages
  const isLoadingMore = isValidating && size > (data?.length || 0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 50 && !isReachingEnd && !isValidating) {
      setSize(size + 1)
    }
  }

  const handleAddDiscount = (discount: DiscountRaw) => {
    const currentIds = customStackableDiscountIds || []
    if (!currentIds.includes(discount.coupon_id)) {
      setFormState({
        prerequisite: {
          ...prerequisite,
          stackability: {
            stackMode: DiscountStackMode.CUSTOM,
            customStackableDiscountIds: [...currentIds, discount.coupon_id]
          }
        }
      })
    }
    setOpen(false)
    setSearchTerm('')
  }

  const handleRemoveDiscount = (id: string) => {
    const currentIds = customStackableDiscountIds || []
    setFormState({
      prerequisite: {
        ...prerequisite,
        stackability: {
          stackMode: DiscountStackMode.CUSTOM,
          customStackableDiscountIds: currentIds.filter(discountId => discountId !== id)
        }
      }
    })
  }

  // Filter out already selected discounts from the suggestion list
  const availableDiscounts = discounts.filter(
    (discount: DiscountRaw) =>
      !customStackableDiscountIds.includes(discount.coupon_id) && discount.coupon_id !== discountId
  )

  const getDiscountCode = (id: string) => {
    const foundInList = discounts.find(discount => discount?.coupon_id === id)
    if (foundInList) return foundInList?.title || foundInList?.code

    const foundInSummaries = summariesData?.data?.find(discount => discount?.coupon_id === id)
    if (foundInSummaries) return foundInSummaries?.title || foundInSummaries?.code

    return id
  }

  return (
    <div className='pb-2' ref={scrollRef}>
      <div className='flex flex-col gap-3 pt-1 pb-4 border-b border-dotted border-zinc-200'>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className='relative'>
              <Input
                placeholder='Search discounts'
                variant='filled'
                startAdornment={<MagnifyingGlass size={18} />}
                value={searchTerm}
                dimension='md'
                onChange={e => setSearchTerm(e.target.value)}
                onClick={() => setOpen(true)}
                className='cursor-pointer'
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className='w-[--radix-popover-trigger-width] p-1 bg-white !border-none !rounded-xl overflow-hidden'
            align='start'
            sideOffset={8}
            onOpenAutoFocus={e => e.preventDefault()}
          >
            <div className='flex flex-col max-h-[300px] overflow-y-auto p-0.5' onScroll={handleScroll}>
              {availableDiscounts.length > 0 ? (
                availableDiscounts.map((discount: DiscountRaw) => (
                  <button
                    key={discount.coupon_id}
                    className='flex items-center justify-between px-3 py-2 hover:bg-zinc-50 transition-colors rounded-md w-full text-left'
                    onClick={() => handleAddDiscount(discount)}
                  >
                    <div className='flex items-center gap-3'>
                      <Icon IconComponent={SealPercent} size='sm' className='text-zinc-400' />
                      <span className='text-text-sm-regular text-zinc-800'>
                        {truncateString(discount.title || discount.code || '', 40)}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='flex justify-end min-w-16'>
                        <span className='px-1.5 text-text-xs-regular text-zinc-500 bg-zinc-500/5 rounded-sm'>
                          {discount.auto_applicable ? 'Automatic' : 'Manual'}
                        </span>
                      </div>
                      <div className='flex justify-end min-w-16'>
                        <span className='px-1.5 text-text-xs-regular text-zinc-500 rounded-sm'>
                          {discount.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className='p-4 text-center text-text-sm-regular text-zinc-400'>
                  {isLoading ? 'Loading discounts...' : 'No discounts found'}
                </div>
              )}
              {isLoadingMore && availableDiscounts.length > 0 && (
                <div className='p-2 text-center text-xs text-zinc-400'>Loading more...</div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {customStackableDiscountIds.length > 0 && (
          <div className='flex gap-2 w-full'>
            <div className='border-y-1 border-l-1 border-zinc-200 w-5 rounded-l-lg'></div>
            <div className='flex flex-col overflow-hidden w-full py-2'>
              {customStackableDiscountIds.map((id: string) => (
                <div key={id} className='flex items-center justify-between py-1.5'>
                  <div className='flex items-center gap-2'>
                    <Icon IconComponent={Tag} size='sm' className='text-zinc-400' iconProps={{ weight: 'duotone' }} />
                    <span className='text-text-sm-regular text-zinc-800'>{getDiscountCode(id)}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <IconButton
                      IconComponent={Trash}
                      onClick={() => handleRemoveDiscount(id)}
                      size='xs'
                      variant='ghost'
                      className='text-zinc-500'
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className='border-y-1 border-r-1 border-zinc-200 w-5 rounded-r-lg'></div>
          </div>
        )}
      </div>
      <FormErrorText message={errors['prerequisite.stackability.customStackableDiscountIds']} />
    </div>
  )
}

export default CustomDiscountSelector
